import { createChart, IChartApi, ISeriesApi, MouseEventParams, Time, SeriesDefinition, ColorType } from "lightweight-charts";
import { Chart } from "../../models/charts/chart";
import { TimeRange } from "../../models/time-range.enum";
import { Series, SeriesTypeEnum, isCustomSeries } from "../../models/series";

import { chartService } from "../../services/charts.service"
import { ChartLegendComponent } from "../chart-legend/chart-legend.component"
import { SeriesListComponent } from "../chart-series/chart-series-list.component"
import { TimeRangeComponent, TimeRangeChangeRequestEvent } from "../time-range.component/chart-time-range.component";
import { StackedAreaSeries } from "../../options/lightweight-charts/stacked-area-series-plugin/stacked-area-series";
import { transformToStackedAreaData } from "../../data/data-object/stacked-area.data";
import { ChartType } from "../../models/charts/chart-type.enum";
import { LoaderComponent } from "../loader/loader-component"
import { authService, AuthService } from "../../../core/services/auth-service";
import { StackedAreaColor } from "../../models/colors.values";
import { seriesOptionsMap } from "../chart-series/series-options.values";

// @ts-ignore
import styleCSS from "./chart.style.scss?inline"
// @ts-ignore
import templateHTML from './chart.component.html?raw'

export class ChartComponent extends HTMLElement {
  private _setSeriesFn: Function
  // TODO remove once validated
  private _viewSeriesMap: Map<string, ISeriesApi<any>> = new Map()
  private _chart: Chart
  private _view: IChartApi
  private _legend: ChartLegendComponent
  private _seriesList: SeriesListComponent
  private _timeRangeComponent: TimeRangeComponent
  private _sourceDiv: HTMLDivElement
  private _graphBottom: HTMLDivElement
  private _graphWrapper: HTMLDivElement
  private _loader: LoaderComponent
  private _authService: AuthService = authService
  private _graphTop: HTMLDivElement

  connectedCallback() {
    const shadowRoot = this.attachShadow({ mode: 'open' })
    const container = document.createElement('div')
    container.innerHTML = templateHTML
    const template = container.querySelector('template')
    const style = document.createElement('style')
    style.textContent = styleCSS
    shadowRoot.appendChild(style)
    shadowRoot.appendChild(template!.content.cloneNode(true))

    const chartTypeAttribute = this.attributes.getNamedItem('chart-type')
    const seriesTypeAttribute = this.attributes.getNamedItem('series-Type')
    const timeRangeAttribute = this.attributes.getNamedItem('time-range')
    const autoloadAttribute = this.attributes.getNamedItem('auto-load')

    if (!chartTypeAttribute || !seriesTypeAttribute) {
      throw new Error('missing chart and/or series type attributs')
    }

    const chartType = chartTypeAttribute.value as ChartType
    const seriesType = seriesTypeAttribute.value as SeriesTypeEnum
    const timeRange = timeRangeAttribute?.value as TimeRange || TimeRange.Month
    const autoLoad = autoloadAttribute?.value || "true"

    this.setupGUI(shadowRoot)
    this._chart = new Chart(chartType as ChartType, seriesType as SeriesTypeEnum, timeRange as TimeRange)
    if (autoLoad !== 'false') { this.init() }

    document.addEventListener('authChange', function (e: Event): void {
      this.onAuthChanged(e)
    }.bind(this))

    document.addEventListener('theme-changed', this.onThemeChanged.bind(this))
  }

  onAuthChanged(_: Event): void {
    this.refresh()
  }

  private getCssVariable(variable: string) {
    return getComputedStyle(document.body).getPropertyValue(variable);
  }

  onThemeChanged(e: Event): void {
    this._view.applyOptions({
      layout: {
        background: {
          type: ColorType.Solid, color: this.getCssVariable("--seashell")
        },
        textColor: this.getCssVariable("--midnight-blue")
      },
      grid: {
        horzLines: {
          visible: true,
          color: this.getCssVariable("--light-grey")
        },
        vertLines: {
          visible: true,
          color: this.getCssVariable("--light-grey")
        }
      }
    })
    this.refresh()
  }

  setupGUI(shadowRoot: ShadowRoot): void {
    this._graphTop = shadowRoot.querySelector('.graph-top')!
    this._graphWrapper = shadowRoot.querySelector('.graph-wrapper')!
    this._legend = shadowRoot.querySelector('tbw-chart-legend') as ChartLegendComponent
    this._graphBottom = shadowRoot.querySelector('.graph-bottom')!
    this._timeRangeComponent = shadowRoot.querySelector('tbw-chart-time-range')!
    this._sourceDiv = shadowRoot.querySelector('.source')!
    this._timeRangeComponent.addEventListener(TimeRangeChangeRequestEvent.EVENT_NAME, this.timeChangeRequestHandler.bind(this))
      
    const loaderElement = shadowRoot.querySelector('.tbw-loader') as HTMLElement;
    this._loader = new LoaderComponent(loaderElement);
  }

  timeChangeRequestHandler(e: Event) {
    this.setTimeRange((e as TimeRangeChangeRequestEvent).timeRange)
  }

  disconnectedCallback() {
    this._timeRangeComponent.removeEventListener(TimeRangeChangeRequestEvent.EVENT_NAME, this.timeChangeRequestHandler)
    document.removeEventListener('authChange', this.init.bind(this))
  }

  async init() {
    this._setSeriesFn = this.isCustomSeries() ? this.addCustomSeries : this.addSeriesArray

    this._view = createChart(this._graphWrapper, this._chart.chartOptions)
    
    if (this._legend) {
      this._view.subscribeCrosshairMove((param: MouseEventParams<Time>) =>
      this._legend.update(param, this._chart, this._viewSeriesMap, this._view.options().localization.priceFormatter!))
    }

    await this.refresh()

    if (this._chart.series.length > 1) {
      this._seriesList = new SeriesListComponent()
      this._graphTop.appendChild(this._seriesList)
      this._seriesList.init(this._chart.series)
    }
  }

  async refresh() {
    await this.load()
    if (!authService.hasFullAccess('charts', '')) {
      this._chart.timeRange = TimeRange.Month
    }
    //this._chart.sortSeriesByMax()
    this.bindToView()
  }

  private async load() {
    this._loader.show()
    await chartService.load(this._chart)
    this._loader.hide()
  }

  get series(): Array<ISeriesApi<any>> {
    return Array.from(this._viewSeriesMap.values())
  }

  getHiddenSeriesNames(viewSeriesMap: Map<string, ISeriesApi<any>>): string[] {
    const hiddenSeries: string[] = []
    for (const [name, series] of viewSeriesMap) {
      if (!series.options().visible) { hiddenSeries.push(name) }
    }
    return hiddenSeries
  }

  private bindToView() {
    this._timeRangeComponent.setAttribute(TimeRangeComponent.ATTRIBUTE_AUTH, this._authService.hasFullAccess('charts').toString())
    this._timeRangeComponent.setAttribute(TimeRangeComponent.SELECTED_ATTR, this._chart.timeRange)

    // backup and restore series hidden states
    const hiddenSeriesNames: string[] = this.getHiddenSeriesNames(this._viewSeriesMap)

    this.clearSeries()
    this._setSeriesFn((this._chart.series))

    hiddenSeriesNames.forEach(s => this._viewSeriesMap.get(s)!.applyOptions({ visible: false }))
    this._view.timeScale().fitContent()
    this._sourceDiv.innerHTML = `<p>source: ${this._chart.source}</p>`
  }

  private addSeriesArray(array: Series[]) {
    array.forEach(this.addSeries.bind(this))
  }
  // custom series is just one series even if multiple series in model
  private addCustomSeries(seriesArray: Series[]) {
    const viewSeries = this._view.addCustomSeries(new StackedAreaSeries())
    viewSeries.setData(transformToStackedAreaData(seriesArray))
    //TODO use model values
    viewSeries.applyOptions({
      colors: seriesArray.map(s => ({ color: s.options.color, line: s.options.color, area: s.options.color })) as StackedAreaColor[],
    })
    this._viewSeriesMap.set('stacked', viewSeries)
    return viewSeries
  }

  private isCustomSeries(): boolean {
    return isCustomSeries(this._chart.seriesDefinitions)
  }

  private addSeries(s: Series): ISeriesApi<any> {
    const viewSeries: ISeriesApi<any> = this._view.addSeries(this._chart.seriesDefinitions as SeriesDefinition<any>, this.generateSeriesOptions(s))
    viewSeries.setData(s.points)
    this._viewSeriesMap.set(s.name, viewSeries)
    return viewSeries
  }

  private clearSeries() {
    for (const [_, series] of this._viewSeriesMap) { this._view.removeSeries(series) }
    this._viewSeriesMap.clear()
  }

  async setTimeRange(timeRange: TimeRange): Promise<void> {
    this._chart.timeRange = timeRange
    await this.refresh()
  }

  private generateSeriesOptions(series: Series) {
    return Object.assign({}, series.options, seriesOptionsMap.get(this._chart.seriesType))
  }
}